import { ProgressCompletionSchema, progressWithCompletion, countsTowardProgress } from './progress.js';
import { z } from 'zod';
/** Promise lifecycle status (FR-409). */
export const PromiseStatus = z.enum(['open', 'in_progress', 'met', 'missed', 'cancelled']);
/**
 * A commitment made to a customer. Named `CustomerPromise` (not `Promise`) to
 * avoid shadowing the JS global `Promise` in generated types.
 *
 * `createdAt` is the "added on" date; `targetDate` is the target completion date.
 * Status is ticket-derived on reads (draft/open/in_progress/met); explicit cancelled is preserved.
 * `ticketCount`/`doneTicketCount`/`progressPct`/`overdue` are derived rollups, present on reads.
 */
export const CustomerPromise = z.object({
    id: z.string(),
    customerId: z.string(),
    customerName: z.string().nullable(), // resolved on reads (handy for the global list)
    title: z.string(),
    description: z.string().nullable(),
    status: PromiseStatus.or(z.literal('draft')),
    targetDate: z.string().nullable(), // ISO date
    createdAt: z.string(),
    updatedAt: z.string(),
    // Progress rollup (progress.ts): linked tickets minus cancelled/rejected ones.
    ticketCount: z.number().optional(),
    doneTicketCount: z.number().optional(),
    completion: ProgressCompletionSchema.optional(),
    ballHolders: z.array(z.object({ userId: z.string(), userName: z.string() })).optional(),
    progressPct: z.number().optional(), // 0-100, mean status weight of those tickets
    blockedTicketCount: z.number().optional(), // linked tickets flagged blocked
    overdue: z.boolean().optional(),
});
export const CreatePromiseInput = z.object({
    title: z.string().min(1).max(300),
    description: z.string().optional(),
    status: PromiseStatus.optional(),
    targetDate: z.string().optional(), // ISO date
});
export const UpdatePromiseInput = CreatePromiseInput.partial().extend({
    // Nullable so the target date can be CLEARED via edit.
    targetDate: z.string().nullable().optional(),
    // Full-replace set of tickets linked to this promise (accepts ids or codes).
    // [] unlinks all. Reconciled against Ticket.promiseId server-side.
    ticketIds: z.array(z.string()).optional(),
});
/** Current commitment status; cancellation is the sole explicit lifecycle override. */
export function promiseProgress(tickets) {
    const progress = progressWithCompletion(tickets);
    const unstarted = new Set(['draft', 'submitted', 'triage', 'backlog', 'design_needed', 'acceptance_criteria_needed', 'estimate_needed', 'needs_approval', 'approved', 'approved_for_agent']);
    const started = tickets.some(t => countsTowardProgress(t.status) && !unstarted.has(t.status));
    const status = tickets.length === 0 ? 'draft' : progress.completion.state === 'complete' ? 'met' : started ? 'in_progress' : 'open';
    return { ...progress, status };
}
//# sourceMappingURL=promise.js.map